import type { AcaoRemota, TelaNavegador } from '@contefy/shared';
import { ArrowDown, ArrowLeft, ArrowUp, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alerta, Botao } from '../componentes/ui';
import { equipeApi } from './api';

const INTERVALO_MS = 2500;

/**
 * Mesa de intervenção: a tela ao vivo do navegador do agente. A pessoa clica na imagem (o clique
 * vai para a mesma posição no navegador), digita e manda teclas; ao terminar, "Pronto, pode
 * continuar" devolve o controle ao agente, que segue de onde parou.
 */
export function MesaNavegador({
  execucaoId,
  motivo,
  oQueFazer,
  aoFechar,
  aoRetomar,
}: {
  execucaoId: string;
  motivo?: string | null;
  oQueFazer?: string | null;
  aoFechar: () => void;
  aoRetomar: () => void;
}) {
  const [tela, setTela] = useState<(TelaNavegador & { pode_retomar?: boolean }) | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [nota, setNota] = useState('');
  const [retomando, setRetomando] = useState(false);
  const ocupadoRef = useRef(false);

  const atualizar = useCallback(async () => {
    if (ocupadoRef.current) return;
    try {
      const t = await equipeApi.telaNavegador(execucaoId);
      setTela(t);
    } catch (e) {
      setErro((e as Error).message);
    }
  }, [execucaoId]);

  useEffect(() => {
    void atualizar();
    const timer = setInterval(() => void atualizar(), INTERVALO_MS);
    return () => clearInterval(timer);
  }, [atualizar]);

  const agir = async (acao: AcaoRemota) => {
    if (ocupadoRef.current) return;
    ocupadoRef.current = true;
    setOcupado(true);
    setErro(null);
    try {
      const t = await equipeApi.acaoNavegador(execucaoId, acao);
      setTela((antes) => ({ ...antes, ...t }));
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      ocupadoRef.current = false;
      setOcupado(false);
    }
  };

  const clicarImagem = (ev: React.MouseEvent<HTMLImageElement>) => {
    const img = ev.currentTarget;
    const r = img.getBoundingClientRect();
    const largura = tela?.largura ?? img.naturalWidth;
    const altura = tela?.altura ?? img.naturalHeight;
    const x = Math.round(((ev.clientX - r.left) / r.width) * largura);
    const y = Math.round(((ev.clientY - r.top) / r.height) * altura);
    void agir({ tipo: 'clicar_xy', x, y });
  };

  const digitar = async () => {
    if (!texto) return;
    await agir({ tipo: 'digitar', texto });
    setTexto('');
  };

  const retomar = async () => {
    setRetomando(true);
    setErro(null);
    try {
      await equipeApi.retomarExecucao(execucaoId, nota.trim() || undefined);
      aoRetomar();
    } catch (e) {
      setErro((e as Error).message);
      setRetomando(false);
    }
  };

  const semNavegador = tela && !tela.ativo;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-900/95 p-3 text-white sm:p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-lg font-semibold">Navegador do agente</div>
          {motivo && (
            <p className="text-sm text-amber-200">
              <b>Por que parou:</b> {motivo}
            </p>
          )}
          {oQueFazer && (
            <p className="text-sm text-slate-200">
              <b>O que fazer:</b> {oQueFazer}
            </p>
          )}
          {tela?.url && <p className="truncate text-xs text-slate-400">{tela.url}</p>}
        </div>
        <button
          type="button"
          onClick={aoFechar}
          className="rounded-lg p-2 text-slate-300 hover:bg-slate-800 hover:text-white"
          aria-label="Fechar sem continuar"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {erro && <Alerta tipo="erro">{erro}</Alerta>}

      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row">
        <div className="relative min-h-0 flex-1 overflow-auto rounded-xl bg-black">
          {tela?.jpeg_base64 ? (
            // biome-ignore lint/a11y/useKeyWithClickEvents: a imagem é a própria tela remota; o teclado vai pela caixa ao lado
            <img
              src={`data:image/jpeg;base64,${tela.jpeg_base64}`}
              alt="Tela do navegador do agente"
              onClick={clicarImagem}
              draggable={false}
              className={`mx-auto block max-h-full w-full select-none object-contain ${ocupado ? 'cursor-progress opacity-70' : 'cursor-crosshair'}`}
            />
          ) : (
            <div className="flex h-full min-h-64 items-center justify-center p-6 text-center text-sm text-slate-300">
              {semNavegador
                ? (tela?.motivo ??
                  'Esta execução não tem navegador aberto. Feche esta tela e rode o agente de novo.')
                : 'Carregando a tela…'}
            </div>
          )}
        </div>

        <div className="w-full shrink-0 space-y-3 lg:w-80">
          <div className="rounded-xl bg-slate-800 p-3 text-sm">
            <p className="mb-2 text-slate-300">
              Clique direto na imagem para clicar no navegador. Para escrever, clique no campo na
              imagem e depois digite aqui:
            </p>
            <div className="flex gap-2">
              <input
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void digitar();
                }}
                placeholder="Texto a digitar"
                className="min-w-0 flex-1 rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-white placeholder:text-slate-500"
                disabled={!tela?.ativo || ocupado}
              />
              <Botao
                variante="secundario"
                onClick={digitar}
                disabled={!texto || !tela?.ativo || ocupado}
              >
                Digitar
              </Botao>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {(
                [
                  ['Enter', 'Enter'],
                  ['Tab', 'Tab'],
                  ['Esc', 'Escape'],
                  ['Apagar', 'Backspace'],
                ] as const
              ).map(([rotulo, tecla]) => (
                <button
                  key={tecla}
                  type="button"
                  disabled={!tela?.ativo || ocupado}
                  onClick={() => void agir({ tipo: 'tecla', tecla })}
                  className="rounded-lg bg-slate-700 px-2.5 py-1.5 text-xs font-medium hover:bg-slate-600 disabled:opacity-50"
                >
                  {rotulo}
                </button>
              ))}
              <button
                type="button"
                disabled={!tela?.ativo || ocupado}
                onClick={() => void agir({ tipo: 'rolar', dy: -500 })}
                className="rounded-lg bg-slate-700 p-1.5 hover:bg-slate-600 disabled:opacity-50"
                aria-label="Rolar para cima"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={!tela?.ativo || ocupado}
                onClick={() => void agir({ tipo: 'rolar', dy: 500 })}
                className="rounded-lg bg-slate-700 p-1.5 hover:bg-slate-600 disabled:opacity-50"
                aria-label="Rolar para baixo"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={!tela?.ativo || ocupado}
                onClick={() => void agir({ tipo: 'voltar' })}
                className="rounded-lg bg-slate-700 p-1.5 hover:bg-slate-600 disabled:opacity-50"
                aria-label="Voltar uma página"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                disabled={ocupado}
                onClick={() => void atualizar()}
                className="rounded-lg bg-slate-700 p-1.5 hover:bg-slate-600 disabled:opacity-50"
                aria-label="Atualizar a tela"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="rounded-xl bg-slate-800 p-3 text-sm">
            <p className="mb-2 text-slate-300">
              Quando terminar, conte ao agente o que você fez (opcional) e mande continuar. Se o
              login no gov.br foi feito, ele fica guardado no cofre para as próximas vezes.
            </p>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={3}
              placeholder="Ex.: resolvi o captcha e o login entrou; a tela já está no Empresa Fácil."
              className="w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-2 text-white placeholder:text-slate-500"
            />
            <Botao
              className="mt-2 w-full"
              carregando={retomando}
              disabled={tela?.pode_retomar === false}
              onClick={retomar}
            >
              Pronto, pode continuar
            </Botao>
            {tela?.pode_retomar === false && (
              <p className="mt-2 text-xs text-amber-200">
                Esta execução não pode mais ser retomada (o servidor reiniciou ou ela esperou
                demais). Feche e rode o agente de novo.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
